<script setup="">
import { GripVerticalIcon, PlusIcon, XIcon } from "lucide-vue-next";
import { computed, ref } from "vue";
import { VueDraggable } from "vue-draggable-plus";
import { object, string } from "yup";

import ActionButton from "@/Components/ActionButton.vue";
import Alert from "@/Components/Alert.vue";
import Badge from "@/Components/Badge.vue";
import EmojiIcon from "@/Components/Form/EmojiIcon.vue";
import SimpleInput from "@/Components/Form/SimpleInput.vue";
import SimpleLabel from "@/Components/Form/SimpleLabel.vue";
import SimpleSelect from "@/Components/Form/SimpleSelect.vue";
import ConfirmModal from "@/Components/Modal/ConfirmModal.vue";
import SimpleButton from "@/Components/SimpleButton.vue";

import { useValidator } from "@/composables/validator";
import { useList } from "@/utilities/listManager";
import { useForm } from "@inertiajs/vue3";

const props = defineProps({
  campaign: {
    type: Object,
    required: true
  },
  update: {
    type: Function,
    required: true
  }
});

const selectedIndex = ref(-1);
const form = useForm({
  type: "link",
  name: "",
  value: "",
  target: "_self"
});

const schema = object().shape({
  name: string().required().label("버튼 이름"),
  value: string().max(512).required().label("링크")
});

const validator = useValidator(schema);

const validate = () => validator.validateSync(form);

const types = [
  { label: "링크 이동", value: "link", default: true },
  { label: "전화 연결", value: "tel" }
];

const targets = [
  { label: "새 창", value: "_blank", default: true },
  { label: "현재 창", value: "_self" }
];

const list = useList(props.campaign?.settings?.links ?? [], {
  max: 5,
  unique: true
});

const model = computed({
  get() {
    return list.toArray();
  },
  set(value) {
    list.clear();
    list.appendAll(value);

    props.update("settings.links", list.toArray());
  }
});

const reset = () => {
  form.reset();
  validator.reset();
};

const append = ({ close }) => {
  list.put({
    type: form.type,
    name: form.name,
    value: form.value
  });

  props.update("settings.links", list.toArray());

  reset();
  close();
};

const modify = (item, index) => {
  form.type = item.type;
  form.name = item.name;
  form.value = item.value;
  form.target = item.target;
  selectedIndex.value = index;
};

const update = () => {
  const element = list.get(selectedIndex.value);
  element.type = form.type;
  element.name = form.name;
  element.value = form.value;
  element.target = form.target;

  props.update("settings.links", list.toArray());
  reset();
  selectedIndex.value = -1;
};

const deleteLink = (index) => {
  list.remove(index);
};

list.on("changed", () => {
  props.update("settings.links", list.toArray());
});
</script>

<template>
  <div>
    <div class="mt-5">
      <VueDraggable v-model="model" handle=".handle" :animation="150" class="space-y-1">
        <div
          v-for="(item, index) in model"
          :key="index"
          class="relative z-0 rounded-xl border p-2 hover:z-5 hover:border-800 dark:border-300"
          @click="modify(item, index)"
        >
          <div class="flex items-center gap-4">
            <GripVerticalIcon class="handle h-5 w-5 flex-none cursor-move text-400 hover:text-600" />
            <div class="min-w-0 flex-1 overflow-hidden">
              <div class="flex items-center gap-2 leading-tight">
                <span class="font-semibold text-800">{{ item.name }}</span>
              </div>
              <div class="space-x-2 text-sm text-600">
                <Badge v-if="item.type === 'link'" classes="py-0.5 text-500">링크</Badge>
                <Badge v-if="item.type === 'tel'" classes="py-0.5 text-500">전화</Badge>
                <span class="text-xs+">{{ item.value }}</span>
              </div>
            </div>
            <div class="flex flex-none items-center gap-3">
              <ActionButton @click.stop="deleteLink(index)">
                <XIcon class="h-5 w-5 cursor-pointer text-600 hover:text-red-600" />
              </ActionButton>
            </div>
          </div>
        </div>
      </VueDraggable>

      <div v-if="list.isEmpty()">
        <Alert classes="text-center text-sm text-600">사용자 버튼이 없습니다.</Alert>
      </div>
      <!--end::empty-state-->

      <ConfirmModal
        :disabled="list.isMax()"
        title="버튼 추가"
        class="flex-1"
        :validate="validate"
        @before-open="reset"
        @after-close="selectedIndex = -1"
        @confirmed="append"
      >
        <template #button="{ open }">
          <div class="mt-6">
            <SimpleButton v-if="!list.isMax()" classes="w-full text-sm+" @click="open">
              <template #icon>
                <PlusIcon class="mr-1 h-4 w-4" />
              </template>
              버튼 추가
            </SimpleButton>
            <p v-else class="text-sm text-600">더 이상 추가할 수 없습니다.</p>
          </div>
        </template>

        <template #content>
          <div class="space-y-4">
            <div>
              <SimpleSelect v-model="form.type" label="버튼 타입" required :options="types" :error="form.errors.type" />
            </div>

            <div>
              <div class="mb-1 flex justify-between">
                <SimpleLabel>버튼 이름</SimpleLabel>
                <div class="flex items-center space-x-1">
                  <span class="text-xs+ font-semibold">이모티콘</span>
                  <EmojiIcon @select="form.name = form.name + $event.i" />
                </div>
              </div>
              <SimpleInput v-model="form.name" placeholder="예) 고객센터" :error="validator.getFieldError('name')" />
            </div>

            <div>
              <SimpleInput
                v-model="form.value"
                label="버튼 링크"
                max-length="256"
                placeholder="예) https://pintl.so"
                type="url"
                :error="validator.getFieldError('value')"
              />
            </div>

            <div>
              <SimpleSelect v-model="form.target" label="링크 타입" :options="targets" required :error="validator.getFieldError('target')" />
            </div>
          </div>
        </template>
      </ConfirmModal>
      <!--end::add-button-->

      <ConfirmModal
        :show="selectedIndex !== -1"
        title="버튼 수정"
        :validate="validate"
        @before-close="reset"
        @after-close="selectedIndex = -1"
        @confirmed="update"
      >
        <template #content>
          <div class="space-y-4">
            <div>
              <SimpleSelect v-model="form.type" label="버튼 타입" required :options="types" :error="form.errors.type" />
            </div>

            <div>
              <div class="mb-1 flex justify-between">
                <SimpleLabel>버튼 이름</SimpleLabel>
                <div class="flex items-center space-x-1">
                  <span class="text-xs+ font-semibold">이모티콘</span>
                  <EmojiIcon @select="form.name = form.name + $event.i" />
                </div>
              </div>
              <SimpleInput v-model="form.name" placeholder="예) 고객센터" :error="validator.getFieldError('name')" />
            </div>

            <div>
              <SimpleInput
                v-model="form.value"
                :label="form.type === 'link' ? '버튼 링크' : '전화번호'"
                max-length="256"
                :placeholder="form.type === 'link' ? 'https://pintl.so' : '010-0000-0000'"
                type="url"
                :error="validator.getFieldError('value')"
              />
            </div>

            <div v-if="form.type === 'link'">
              <SimpleSelect v-model="form.target" label="링크 타입" :options="targets" required :error="validator.getFieldError('target')" />
            </div>
          </div>
        </template>
      </ConfirmModal>
    </div>
  </div>
</template>
